import React, { useState, useContext, useEffect, useRef } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Typography, Paper, Avatar, AvatarGroup, Chip, IconButton, Tooltip } from '@mui/material';
import { 
  ArrowBack, 
  ArrowForward, 
  CheckCircle, 
  RadioButtonUnchecked,
  People,
  Timeline,
  Assignment,
  Lightbulb,
  Brush,
  ThumbUp,
  Build,
  Science
} from '@mui/icons-material';
import UserResearchPhase from './UserResearchPhase';
import HowMightWePhase from './HowMightWePhase';
import IdeationPhase from './IdeationPhase';
import SketchDrawPhase from './SketchDrawPhase';
import DecisionPhase from './DecisionPhase';
import PrototypingPhase from './PrototypingPhase';
import TestingPhase from './TestingPhase';
import { AuthContext } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../config/firebase';

const steps = [
  'User Research',
  'How Might We',
  'Ideation',
  'Sketch/Draw',
  'Decision',
  'Prototyping',
  'Testing',
];

const stepIcons = [
  <People key="research" />,
  <Lightbulb key="hmw" />,
  <Assignment key="ideation" />,
  <Brush key="sketch" />,
  <ThumbUp key="decision" />,
  <Build key="prototyping" />,
  <Science key="testing" />
];

const phaseDescriptions = [
  'Upload/view personas, pain points, research notes.',
  'Collaborative HMW submission and voting.',
  'Rapid idea submission, real-time clustering/voting.',
  'Digital whiteboard or image upload.',
  'Solution voting and summary.',
  'Attach design files or use a simple wireframe tool.',
  'Add test tasks, collect feedback, display results.',
];

const DesignSprintModule: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]);
  const presenceInterval = useRef<NodeJS.Timeout | null>(null);

  // Real-time presence logic
  useEffect(() => {
    if (!teamId || !sprintId || !currentUser) return;
    const presenceRef = doc(firestore, `teams/${teamId}/designSprints/${sprintId}/presence/${currentUser.uid}`);
    const updatePresence = async () => {
      await setDoc(presenceRef, {
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Unknown',
        photoURL: currentUser.photoURL || '',
        lastActive: serverTimestamp(),
        phase: activeStep
      }, { merge: true });
    };
    updatePresence();
    presenceInterval.current = setInterval(updatePresence, 30000); // update every 30s
    const handleUnload = async () => {
      await deleteDoc(presenceRef);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      if (presenceInterval.current) clearInterval(presenceInterval.current);
      window.removeEventListener('beforeunload', handleUnload);
      deleteDoc(presenceRef);
    };
  }, [teamId, sprintId, currentUser, activeStep]);

  // Listen for presence
  useEffect(() => {
    if (!teamId || !sprintId) return;
    const presenceCol = collection(firestore, `teams/${teamId}/designSprints/${sprintId}/presence`);
    const unsub = onSnapshot(presenceCol, snap => {
      setOnlineUsers(snap.docs.map(doc => doc.data()));
    });
    return () => unsub();
  }, [teamId, sprintId]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Enhanced Header with gradient background */}
      <header className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 rounded-b-3xl pb-8 pt-6 px-6">
        {/* Back button */}
        <button
          onClick={() => navigate(`/teams/${teamId}/design-sprints`)}
          className="absolute top-6 left-6 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
        >
          <ArrowBack className="w-5 h-5" />
        </button>

        {/* Title and description */}
        <div className="mt-12 text-center">
          <h1 className="text-3xl font-bold text-white font-['Poppins'] mb-2">Design Sprint</h1>
          <p className="text-white/80 text-lg font-['Inter'] mb-6">
            Collaborative design thinking process
          </p>
        </div>

        {/* Online users presence */}
        {onlineUsers.length > 0 && (
          <div className="flex justify-center mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
              <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                {onlineUsers.map(user => (
                  <Tooltip key={user.userId} title={`${user.userName} - Online`}>
                    <Avatar 
                      src={user.photoURL} 
                      alt={user.userName}
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        fontSize: '0.75rem',
                        border: '2px solid white'
                      }}
                    >
                      {user.userName?.charAt(0)}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
              <span className="text-white text-sm font-medium">
                {onlineUsers.length} online
              </span>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col p-6">
        {/* Enhanced Progress Stepper */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 font-['Poppins']">
              Sprint Progress
            </h2>
            <Chip 
              label={`Step ${activeStep + 1} of ${steps.length}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </div>
          
          {/* Progress steps: horizontal scroll on mobile, grid on md+ */}
          <div className="flex md:grid md:grid-cols-7 gap-2 overflow-x-auto pb-2">
            {steps.map((label, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isClickable = index <= activeStep + 1; // Allow clicking current and next step
              
              return (
                <div
                  key={label}
                  className={`relative group cursor-pointer transition-all duration-200 flex-shrink-0 md:flex-shrink md:transform-none ${
                    isClickable ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={isClickable ? handleStep(index) : undefined}
                  style={{ minWidth: '80px' }}
                >
                  {/* Step Icon */}
                  <div className={`
                    w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                      : isCompleted 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <div className="w-6 h-6">
                        {React.cloneElement(stepIcons[index], { className: 'w-6 h-6' })}
                      </div>
                    )}
                  </div>
                  
                  {/* Step Label */}
                  <div className="text-center">
                    <p className={`
                      text-xs font-medium transition-colors duration-200
                      ${isActive 
                        ? 'text-blue-600' 
                        : isCompleted 
                          ? 'text-green-600' 
                          : 'text-gray-500'
                      }
                    `}>
                      {label}
                    </p>
                  </div>
                  
                  {/* Progress Line */}
                  {index < steps.length - 1 && (
                    <div className={`
                      hidden md:block absolute top-6 left-full w-full h-0.5 transition-all duration-200
                      ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}
                    `} />
                  )}
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {phaseDescriptions[index]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Enhanced Content Area */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
          {/* Phase Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`
              w-12 h-12 rounded-xl flex items-center justify-center
              bg-gradient-to-r from-blue-500 to-purple-600 text-white
            `}>
              {React.cloneElement(stepIcons[activeStep], { className: 'w-6 h-6' })}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 font-['Poppins']">
                {steps[activeStep]}
              </h3>
              <p className="text-gray-600 font-['Inter']">
                {phaseDescriptions[activeStep]}
              </p>
            </div>
          </div>

          {/* Phase Content */}
          <div className="mb-6">
            {activeStep === 0 && <UserResearchPhase />}
            {activeStep === 1 && <HowMightWePhase />}
            {activeStep === 2 && <IdeationPhase />}
            {activeStep === 3 && <SketchDrawPhase />}
            {activeStep === 4 && <DecisionPhase />}
            {activeStep === 5 && <PrototypingPhase />}
            {activeStep === 6 && <TestingPhase />}
          </div>

          {/* Enhanced Navigation */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-100">
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  backgroundColor: '#eff6ff'
                },
                '&:disabled': {
                  borderColor: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-['Inter']">
                {activeStep + 1} of {steps.length}
              </span>
            </div>
            
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)',
                  color: '#ffffff'
                },
                '&:disabled': {
                  background: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
            >
              {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
            </Button>
          </div>
        </div>
      </main>

      {/* Enhanced Footer */}
      <footer className="text-center text-sm text-gray-500 py-6 font-['Inter']">
        © {new Date().getFullYear()} StyleSync - Collaborative Design Platform
      </footer>
    </div>
  );
};

export default DesignSprintModule; 